import { useCallback, useEffect, useState, useSyncExternalStore } from "react";

import {
  applyTheme,
  readStoredTheme,
  systemTheme,
  writeStoredTheme,
  type Theme,
} from "@/lib/theme";

function subscribeToSystemTheme(onChange: () => void): () => void {
  const query = window.matchMedia("(prefers-color-scheme: dark)");
  query.addEventListener("change", onChange);
  return () => {
    query.removeEventListener("change", onChange);
  };
}

export function useTheme(): {
  theme: Theme;
  resolved: "light" | "dark";
  setTheme: (theme: Theme) => void;
} {
  const [theme, setThemeState] = useState<Theme>(readStoredTheme);

  /**
   * The OS preference as a subscription rather than state kept in sync by an
   * effect. Someone whose machine flips at sunset gets the change without a
   * reload, and React re-renders because the store changed — not because an
   * effect wrote state after the fact.
   */
  const system = useSyncExternalStore(
    subscribeToSystemTheme,
    systemTheme,
    () => "light" as const,
  );

  const resolved = theme === "system" ? system : theme;

  useEffect(() => {
    applyTheme(theme);
  }, [theme, system]);

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
    writeStoredTheme(next);
    applyTheme(next);
  }, []);

  return { theme, resolved, setTheme };
}
